import React, { useCallback } from 'react';

import styled from 'styled-components';

import { ClientFormRecoverPassword } from '@/@types/client';
import Button from '@/components/Button';
import FormInput from '@/components/Form/Input';
import { Loader } from '@/components/Loader';
import { useAuth } from '@/contexts/auth';
import { useFormValues } from '@/hooks/use-form-values';
import { showAlert } from '@/utils';
import { validateCPF } from '@/utils/validations';

const StyledButton = styled(Button)`
  display: block;
  width: 100%;
`;

type Props = {
  onSuccess: () => void;
};

const RecoverPassword = ({ onSuccess }: Props) => {
  const { handleRecoverPassword, shouldInformEmail } = useAuth();

  const {
    formValues,
    loadingFormValues,
    handleChangeFormValues,
    handleClearFormValues,
    handleSubmitFormValues,
  } = useFormValues<ClientFormRecoverPassword>({
    CPF_CLIENTE: '',
    EMAIL_CLIENTE: '',
  });

  const onSubmit = useCallback(
    async (_, values: ClientFormRecoverPassword) => {
      if (!validateCPF(values.CPF_CLIENTE)) {
        return showAlert('CPF inválido', 'error');
      }

      try {
        await handleRecoverPassword(values);
        handleClearFormValues();
        onSuccess();
        // eslint-disable-next-line no-empty
      } catch (err) {
        if (err.response.status !== 406) {
          showAlert(
            'Ocorreu um erro inesperado, por favor verifique os seus dados e tente novamente.',
            'error',
          );
        }
      }
    },
    [handleClearFormValues, handleRecoverPassword, onSuccess],
  );

  return (
    <form onSubmit={handleSubmitFormValues(onSubmit)}>
      <FormInput
        required
        name="CPF_CLIENTE"
        value={formValues.CPF_CLIENTE}
        onChange={handleChangeFormValues}
        mask="###.###.###-##"
        placeholder="Digite seu cpf"
        replace={{ to: /[^0-9]/g }}
      />

      {shouldInformEmail && (
        <FormInput
          type="email"
          name="EMAIL_CLIENTE"
          value={formValues.EMAIL_CLIENTE}
          onChange={handleChangeFormValues}
          placeholder="E-mail"
          required
        />
      )}
      <StyledButton type="submit" disabled={loadingFormValues}>
        {loadingFormValues ? <Loader /> : 'Recuperar'}
      </StyledButton>
    </form>
  );
};

export default React.memo(RecoverPassword);
